/**
 * The WebGL2 mandala renderer (implements MandalaRenderer from ../renderer-api.ts).
 *
 * Deviation from PLAN.md D6: raw WebGL2 instead of React Three Fiber / three.js, to keep the dependency
 * surface small. Per frame, in order: ripple sim → nebula (half-res) → waveform upload → main mandala pass
 * (→ MRT + dual-Kawase bloom + composite on tier 2) → particles. See devPage.md for how the play screen
 * drives it.
 */
import { FIFTHS_ORDER, type Family } from '@tonist/shared';
import { layoutFor, type MandalaLayout } from '../geometry';
import { labelPositions } from '../labels';
import type { MandalaRenderer, SceneState, ZoneActivity } from '../renderer-api';
import { audioLog } from '../../engine/log';
import { AudioTexture } from './audioTexture';
import { BloomChain } from './bloom';
import { bindTextureUnit, createFullscreenQuad, createProgram, createTarget, destroyTarget, type FullscreenQuad, type Program } from './gl';
import { CalmRings, OneShot, PetalUnfold, RingMotion, smoothTowards, type RingActivityInput } from './motion';
import { NebulaPass } from './nebula';
import { COLOURS, FAMILY_INDEX, FAMILY_RGB, masterRampPosition, nebulaContrastFor, rampColour, sunburstFor, type RGB } from './palette';
import { Particles } from './particles';
import { DROP, DropQueue, pathSplats, simFrameFor, toSimUv, type SimFrame } from './ripple';
import { RippleSim } from './rippleSim';
import { MANDALA_FRAG } from './shaders/mandala';
import { QUAD_VERT } from './shaders/quad';
import { decideTier, runBenchmark, tierBudgets, type Tier } from './tier';

const MAX_ZONES = 16;
const MAX_LABELS = 48;
const MAX_POINTERS = 10;

const DEFAULT_SCENE: SceneState = {
  degrees: 7, tonicPc: 1, scaleMask: FIFTHS_ORDER.map(() => false), ringFamilies: ['white', 'blue', 'yellow', 'red'],
  calm: false, theme: 'dark', mode: 'play',
};

interface PointerSlot { id: number; active: boolean; x: number; y: number; lastX: number; lastY: number }

/** Uniform locations of the main pass, resolved once per program. */
interface MainUniforms {
  uCanvasPx: WebGLUniformLocation | null; uCanvasCss: WebGLUniformLocation | null; uDpr: WebGLUniformLocation | null;
  uCentre: WebGLUniformLocation | null; uR: WebGLUniformLocation | null; uRadii: WebGLUniformLocation | null; uPetals: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null; uWall: WebGLUniformLocation | null; uCalm: WebGLUniformLocation | null; uTheme: WebGLUniformLocation | null; uReveal: WebGLUniformLocation | null;
  uPetalOpen: WebGLUniformLocation | null; uRingRot: WebGLUniformLocation | null; uMelt: WebGLUniformLocation | null; uRingCol: WebGLUniformLocation | null;
  uScaleMask: WebGLUniformLocation | null; uScaleMaskPrev: WebGLUniformLocation | null; uTonicSector: WebGLUniformLocation | null;
  uTonicWave: WebGLUniformLocation | null; uScaleMorph: WebGLUniformLocation | null; uTint: WebGLUniformLocation | null; uSunburst: WebGLUniformLocation | null;
  uZoneA: WebGLUniformLocation | null; uZoneB: WebGLUniformLocation | null; uZoneCount: WebGLUniformLocation | null;
  uLabels: WebGLUniformLocation | null; uLabelCount: WebGLUniformLocation | null; uLabelRadius: WebGLUniformLocation | null;
  uCalmRings: WebGLUniformLocation | null; uCalmRingCount: WebGLUniformLocation | null;
  uRipple: WebGLUniformLocation | null; uSimCentre: WebGLUniformLocation | null; uSimSpan: WebGLUniformLocation | null; uSimTexel: WebGLUniformLocation | null; uRefract: WebGLUniformLocation | null;
  uNebula: WebGLUniformLocation | null; uWave: WebGLUniformLocation | null;
}

function resolveUniforms(p: Program): MainUniforms {
  const names = ['uCanvasPx', 'uCanvasCss', 'uDpr', 'uCentre', 'uR', 'uRadii', 'uPetals', 'uTime', 'uWall', 'uCalm', 'uTheme', 'uReveal',
    'uPetalOpen', 'uRingRot', 'uMelt', 'uRingCol', 'uScaleMask', 'uScaleMaskPrev', 'uTonicSector', 'uTonicWave', 'uScaleMorph', 'uTint', 'uSunburst',
    'uZoneA', 'uZoneB', 'uZoneCount', 'uLabels', 'uLabelCount', 'uLabelRadius', 'uCalmRings', 'uCalmRingCount',
    'uRipple', 'uSimCentre', 'uSimSpan', 'uSimTexel', 'uRefract', 'uNebula', 'uWave'] as const;
  const out: Record<string, WebGLUniformLocation | null> = {};
  for (const n of names) out[n] = p.loc(n);
  return out as unknown as MainUniforms;
}

/** Everything that lives only while a GL context is alive; rebuilt on context restore. */
interface GlState {
  gl: WebGL2RenderingContext;
  quad: FullscreenQuad;
  main: Program;
  U: MainUniforms;
  ripple: RippleSim;
  nebula: NebulaPass;
  audio: AudioTexture;
  bloom: BloomChain | null;
  particles: Particles | null;
}

export function createMandalaRenderer(): MandalaRenderer {
  // ------------------------------------------------------------------ persistent state
  let canvas: HTMLCanvasElement | null = null;
  let S: GlState | null = null;
  let tier: Tier = 1;
  let tierDecided = false;
  let available = true;
  let lost = false;

  let cssW = 1, cssH = 1, dpr = 1, devW = 1, devH = 1;
  let gpu = '';
  let benchMs = NaN;
  let layout: MandalaLayout = layoutFor(1, 1);
  let sim: SimFrame = simFrameFor(1, 1);

  let scene: SceneState = DEFAULT_SCENE;
  let viewCalm = false;
  let zones: readonly ZoneActivity[] = [];

  // uniform staging buffers (no per-frame allocation)
  const zoneA = new Float32Array(MAX_ZONES * 4);
  const zoneB = new Float32Array(MAX_ZONES * 4);
  const labels = new Float32Array(MAX_LABELS * 2);
  let labelCount = 0;
  let labelDegrees = -1;
  const radii = new Float32Array(4);
  const scaleMask = new Float32Array(12);
  const scaleMaskPrev = new Float32Array(12);
  const ringCol = new Float32Array(12);
  const tint: RGB = [1, 0.88, 0.54];
  const ringInputs: RingActivityInput[] = Array.from({ length: 5 }, () => ({ latched: false, family: 'white' as Family }));
  const ringLatchedNow = [false, false, false, false, false];
  const ringFamilyNow: Family[] = ['white', 'blue', 'yellow', 'red', 'white'];

  // motion
  const petals = new PetalUnfold();
  const rings = new RingMotion();
  const tonicWave = new OneShot(1.2);
  const scaleMorph = new OneShot(1.5);
  const calmRings = new CalmRings();
  let reveal = 0, droste = 0;
  let animTime = 0, nebTime = 0;
  let lastMs = -1;
  let frameIndex = 0;
  let sizeDirty = true;

  // master colour
  let centroidTarget = 0, rampPos = 0, voiceCount = 0, sunburst = 0, contrast = 1;

  // pointers + ripple drops
  const pointers: PointerSlot[] = Array.from({ length: MAX_POINTERS }, () => ({ id: -1, active: false, x: 0, y: 0, lastX: 0, lastY: 0 }));
  const drops = new DropQueue();

  const perf = { avgFrameMs: 16.7, frames: 0 };
  const readback = new Uint8Array(4);

  // ------------------------------------------------------------------ helpers
  function findPointer(id: number): PointerSlot | undefined {
    for (const p of pointers) if (p.active && p.id === id) return p;
    return undefined;
  }
  function claimPointer(id: number): PointerSlot | undefined {
    const existing = findPointer(id);
    if (existing) return existing;
    for (const p of pointers) if (!p.active) { p.id = id; p.active = true; return p; }
    return undefined;
  }

  function isCalm(): boolean { return scene.calm || viewCalm; }

  function tonicSector(): number { return FIFTHS_ORDER.indexOf(scene.tonicPc); }

  /** Set the css size / dpr and derive layout, sim frame and radii (no GL work). */
  function setLayout(w: number, h: number, ratio: number): void {
    cssW = Math.max(1, w); cssH = Math.max(1, h); dpr = Math.max(0.5, ratio);
    devW = Math.max(1, Math.round(cssW * dpr));
    devH = Math.max(1, Math.round(cssH * dpr));
    layout = layoutFor(cssW, cssH);
    sim = simFrameFor(cssW, cssH);
    for (let i = 0; i < 4; i++) radii[i] = layout.radii[i]!;
    sizeDirty = true;
  }

  function refreshLabels(): void {
    if (labelDegrees === scene.degrees && !sizeDirty) return;
    const pos = labelPositions(layout, scene.degrees);
    labelCount = Math.min(MAX_LABELS, pos.length);
    for (let i = 0; i < labelCount; i++) {
      labels[i * 2] = pos[i]!.x / cssW;
      labels[i * 2 + 1] = pos[i]!.y / cssH;
    }
    labelDegrees = scene.degrees;
  }

  function splatDown(x: number, y: number, first: boolean): void {
    const [u, v] = toSimUv(sim, x, y);
    if (first) drops.push(u, v, DROP.firstRadius, DROP.firstStrength);
    else drops.push(u, v, DROP.downRadius, DROP.downStrength);
  }

  function firstTouch(nowSec: number, x: number, y: number): void {
    if (petals.started) return;
    if (isCalm()) petals.forceOpen(); else petals.begin(nowSec);
    splatDown(x, y, true);
  }

  // ------------------------------------------------------------------ GL setup / teardown
  function buildGl(c: HTMLCanvasElement): GlState | null {
    const gl = c.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    if (!gl) return null;
    try {
      const ext = gl.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_RENDERER_WEBGL: number } | null;
      gpu = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    } catch { gpu = 'unknown'; }
    const quad = createFullscreenQuad(gl);
    const audio = new AudioTexture(gl);
    const baseDefines: string[] = audio.unorm ? ['WAVE_UNORM'] : [];
    // the ripple sim's storage decides a define of the main pass, so it comes first
    let ripple = new RippleSim(gl, quad, tierBudgets(tier).rippleSize);
    if (ripple.packed) baseDefines.push('RIPPLE_PACKED');

    if (!tierDecided) {
      // ~150 ms micro-benchmark: the main shader (non-MRT) at 512²
      const bench = createProgram(gl, QUAD_VERT, MANDALA_FRAG, baseDefines);
      const target = createTarget(gl, 512, 512, { internalFormat: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, filter: gl.NEAREST });
      const U = resolveUniforms(bench);
      const sw = cssW, sh = cssH, sd = dpr, pixels = Math.max(1, devW * devH);
      setLayout(512, 512, 1);
      let ms = Infinity;
      try {
        ms = runBenchmark(
          () => {
            gl.bindFramebuffer(gl.FRAMEBUFFER, target.fb);
            gl.viewport(0, 0, 512, 512);
            bench.use();
            uploadMainUniforms(gl, U, ripple, audio, null, 512, 512, 0, 0);
            quad.draw();
          },
          () => { gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, readback); },
          () => performance.now(),
          150,
        );
      } catch { ms = Infinity; }
      setLayout(sw, sh, sd);
      destroyTarget(gl, target);
      bench.destroy();
      const nav = navigator as Navigator & { deviceMemory?: number };
      tier = decideTier({ msPer512: ms, concurrency: navigator.hardwareConcurrency ?? 0, memoryGB: nav.deviceMemory, pixels });
      tierDecided = true;
      benchMs = ms;
      audioLog.log('context', () => `renderer: tier ${tier} (bench ${Number.isFinite(ms) ? ms.toFixed(2) + ' ms per 512²' : 'failed'}, ${pixels} px, ${navigator.hardwareConcurrency ?? '?'} cores, ${nav.deviceMemory ?? '?'} GB), gpu "${gpu}"`);
      const wantSize = tierBudgets(tier).rippleSize;
      if (wantSize !== ripple.size) { ripple.destroy(); ripple = new RippleSim(gl, quad, wantSize); }
    }
    const budgets = tierBudgets(tier);
    const main = createProgram(gl, QUAD_VERT, MANDALA_FRAG, budgets.bloom ? [...baseDefines, 'MRT'] : baseDefines);
    const state: GlState = {
      gl, quad, main, U: resolveUniforms(main), ripple, audio,
      nebula: new NebulaPass(gl, quad),
      bloom: budgets.bloom ? new BloomChain(gl, quad) : null,
      particles: budgets.particles > 0 ? new Particles(gl, budgets.particles) : null,
    };
    sizeDirty = true;
    return state;
  }

  function destroyGl(): void {
    if (!S) return;
    if (!S.gl.isContextLost()) {
      S.particles?.destroy(); S.bloom?.destroy(); S.nebula.destroy(); S.audio.destroy(); S.ripple.destroy(); S.main.destroy(); S.quad.destroy();
    }
    S = null;
  }

  function onLost(e: Event): void { e.preventDefault(); lost = true; S = null; }
  function onRestored(): void {
    if (!canvas) return;
    lost = false;
    try { S = buildGl(canvas); } catch (err) { console.error('[mandala] rebuild after context restore failed', err); S = null; }
    applySize();
  }

  /** Apply the current layout to the canvas buffer and the size-dependent targets. */
  function applySize(): void {
    if (!canvas) return;
    setLayout(cssW, cssH, dpr);
    if (canvas.width !== devW) canvas.width = devW;
    if (canvas.height !== devH) canvas.height = devH;
    if (S) { S.nebula.resize(devW, devH); S.bloom?.resize(devW, devH); }
  }

  // ------------------------------------------------------------------ uniform upload for the main pass
  function uploadMainUniforms(gl: WebGL2RenderingContext, U: MainUniforms, ripple: RippleSim, audio: AudioTexture, nebulaTex: WebGLTexture | null, w: number, h: number, nowSec: number, wallSec: number): void {
    const calm = isCalm() ? 1 : 0;
    gl.uniform2f(U.uCanvasPx, w * dpr, h * dpr);
    gl.uniform2f(U.uCanvasCss, w, h);
    gl.uniform1f(U.uDpr, dpr);
    gl.uniform2f(U.uCentre, layout.cx, layout.cy);
    gl.uniform1f(U.uR, layout.R);
    gl.uniform1fv(U.uRadii, radii);
    gl.uniform1f(U.uPetals, scene.degrees + 1);
    gl.uniform1f(U.uTime, nowSec);
    gl.uniform1f(U.uWall, wallSec);
    gl.uniform1f(U.uCalm, calm);
    gl.uniform1f(U.uTheme, scene.theme === 'light' ? 1 : 0);
    gl.uniform1f(U.uReveal, reveal);
    gl.uniform1fv(U.uPetalOpen, petals.open);
    gl.uniform1fv(U.uRingRot, rings.angle);
    gl.uniform1fv(U.uMelt, rings.melt);
    gl.uniform3fv(U.uRingCol, ringCol);
    gl.uniform1fv(U.uScaleMask, scaleMask);
    gl.uniform1fv(U.uScaleMaskPrev, scaleMaskPrev);
    gl.uniform1f(U.uTonicSector, Math.max(0, tonicSector()));
    gl.uniform1f(U.uTonicWave, tonicWave.progress(nowSec));
    gl.uniform1f(U.uScaleMorph, scaleMorph.progress(nowSec));
    gl.uniform3f(U.uTint, tint[0], tint[1], tint[2]);
    gl.uniform1f(U.uSunburst, sunburst);
    const zc = Math.min(MAX_ZONES, zones.length);
    gl.uniform1i(U.uZoneCount, zc);
    if (zc > 0) { gl.uniform4fv(U.uZoneA, zoneA, 0, zc * 4); gl.uniform4fv(U.uZoneB, zoneB, 0, zc * 4); }
    gl.uniform1i(U.uLabelCount, labelCount);
    if (labelCount > 0) gl.uniform2fv(U.uLabels, labels, 0, labelCount * 2);
    gl.uniform1f(U.uLabelRadius, Math.max(14, layout.R * 0.055));
    gl.uniform1i(U.uCalmRingCount, calmRings.count);
    if (calmRings.count > 0) gl.uniform4fv(U.uCalmRings, calmRings.data, 0, calmRings.count * 4);
    gl.uniform2f(U.uSimCentre, sim.cx, sim.cy);
    gl.uniform1f(U.uSimSpan, sim.span);
    gl.uniform1f(U.uSimTexel, 1 / ripple.size);
    gl.uniform1f(U.uRefract, calm ? 0 : 34 * (layout.R / 400));
    bindTextureUnit(gl, 0, ripple.texture, U.uRipple);
    bindTextureUnit(gl, 1, nebulaTex, U.uNebula);
    bindTextureUnit(gl, 2, audio.texture, U.uWave);
  }

  // ------------------------------------------------------------------ per-frame state packing
  function packZones(): void {
    for (let k = 0; k < 5; k++) { ringLatchedNow[k] = false; }
    const n = Math.min(MAX_ZONES, zones.length);
    for (let i = 0; i < n; i++) {
      const z = zones[i]!;
      const o = i * 4;
      zoneA[o] = z.ring; zoneA[o + 1] = z.index; zoneA[o + 2] = z.slot; zoneA[o + 3] = Math.min(1, Math.max(0, z.amplitude));
      zoneB[o] = z.age; zoneB[o + 1] = z.latched ? 1 : 0; zoneB[o + 2] = z.releasing ? 1 : 0; zoneB[o + 3] = FAMILY_INDEX[z.family] ?? 0;
      const sustained = z.latched || (scene.mode === 'continuous' && z.amplitude > 0.01 && !z.releasing);
      if (sustained && !z.releasing) { ringLatchedNow[z.ring] = true; ringFamilyNow[z.ring] = z.family; }
    }
    for (let k = 0; k < 5; k++) {
      ringInputs[k]!.latched = ringLatchedNow[k]!;
      ringInputs[k]!.family = ringLatchedNow[k] ? ringFamilyNow[k]! : (k < 4 ? scene.ringFamilies[k as 0 | 1 | 2 | 3] : 'white');
    }
  }

  function updateMaster(dt: number): void {
    const targetPos = masterRampPosition(centroidTarget);
    rampPos = smoothTowards(rampPos, targetPos, dt, 0.3);
    const c = rampColour(rampPos, tint);
    // keep highlights warm: blend the ramp with gold highlight
    tint[0] = c[0] * 0.6 + COLOURS.goldHi[0] * 0.4;
    tint[1] = c[1] * 0.6 + COLOURS.goldHi[1] * 0.4;
    tint[2] = c[2] * 0.6 + COLOURS.goldHi[2] * 0.4;
    sunburst = smoothTowards(sunburst, sunburstFor(voiceCount), dt, 1.5);
    contrast = smoothTowards(contrast, nebulaContrastFor(voiceCount), dt, 1.0);
  }

  // ------------------------------------------------------------------ public API
  const renderer: MandalaRenderer = {
    get tier() { return tier; },
    get available() { return available; },
    perf,
    diagnostics() { return { tier, gpu, benchMsPer512: benchMs, dpr, cssWidth: cssW, cssHeight: cssH }; },

    mount(c) {
      if (canvas) renderer.unmount();
      canvas = c;
      lost = false;
      canvas.addEventListener('webglcontextlost', onLost, false);
      canvas.addEventListener('webglcontextrestored', onRestored, false);
      const rect = c.getBoundingClientRect();
      setLayout(rect.width > 0 ? rect.width : cssW, rect.height > 0 ? rect.height : cssH, Math.min(2, window.devicePixelRatio || 1));
      try {
        S = buildGl(c);
        available = S !== null;
      } catch (err) {
        console.error('[mandala] WebGL2 setup failed', err);
        S = null;
        available = false;
      }
      applySize();
    },

    unmount() {
      if (!canvas) return;
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      destroyGl();
      canvas = null;
      lastMs = -1;
    },

    resize(w, h, ratio) {
      setLayout(w, h, ratio);
      applySize();
    },

    setScene(next) {
      const maskChanged = next.scaleMask.some((v, i) => (v ? 1 : 0) !== scaleMask[i]) || next.tonicPc !== scene.tonicPc;
      if (maskChanged) scaleMaskPrev.set(scaleMask);
      for (let i = 0; i < 12; i++) scaleMask[i] = next.scaleMask[i] ? 1 : 0;
      for (let k = 0; k < 4; k++) {
        const fam = FAMILY_RGB[next.ringFamilies[k as 0 | 1 | 2 | 3]] ?? FAMILY_RGB.white;
        ringCol[k * 3] = fam[0]; ringCol[k * 3 + 1] = fam[1]; ringCol[k * 3 + 2] = fam[2];
      }
      scene = next;
      refreshLabels();
    },

    setCalm(calm) { viewCalm = calm; },

    setPrayerWheel(omega) { rings.setWheel(omega); },

    setZoneActivity(z) { zones = z; },

    setWaveform(slot, data) { S?.audio.set(slot, data); },

    setMaster(centroidHz, voices) { centroidTarget = centroidHz; voiceCount = voices; },

    pointerDown(id, x, y) {
      const nowSec = lastMs >= 0 ? lastMs / 1000 : performance.now() / 1000;
      const p = claimPointer(id);
      if (!p) return;
      p.x = p.lastX = x; p.y = p.lastY = y;
      if (!petals.started) firstTouch(nowSec, x, y); else splatDown(x, y, false);
      if (isCalm()) calmRings.add(x, y, performance.now() / 1000, 0.6);
    },

    pointerMove(id, x, y) {
      const p = findPointer(id);
      if (!p) return;
      if (!isCalm()) pathSplats(drops, sim, p.x, p.y, x, y);
      p.lastX = p.x; p.lastY = p.y;
      p.x = x; p.y = y;
    },

    pointerUp(id, x, y) {
      const p = findPointer(id);
      if (p) { p.active = false; p.id = -1; }
      if (isCalm()) { calmRings.add(x, y, performance.now() / 1000, 1.0); return; }
      const [u, v] = toSimUv(sim, x, y);
      drops.push(u, v, DROP.upRadius, DROP.upStrength);
    },

    pulse(kind) {
      const nowSec = lastMs >= 0 ? lastMs / 1000 : performance.now() / 1000;
      switch (kind) {
        case 'first-touch': firstTouch(nowSec, layout.cx, layout.cy); break;
        case 'tonic-change': tonicWave.trigger(nowSec); break;
        case 'scale-change': scaleMorph.trigger(nowSec); break;
        case 'latch': rings.pulse('latch'); break;
        case 'unlatch': rings.pulse('unlatch'); break;
      }
    },

    frame(timeMs) {
      const st = S;
      if (!st || lost || !canvas) return;
      const gl = st.gl;
      if (gl.isContextLost()) return;
      const nowSec = timeMs / 1000;
      const dt = lastMs < 0 ? 1 / 60 : Math.min(0.1, Math.max(0, (timeMs - lastMs) / 1000));
      if (lastMs >= 0) {
        const ms = timeMs - lastMs;
        perf.avgFrameMs += (ms - perf.avgFrameMs) * Math.min(1, ms / 1000);
      }
      perf.frames++;
      lastMs = timeMs;
      frameIndex++;
      const calm = isCalm();
      const budgets = tierBudgets(tier);

      // ---- animation state
      if (!calm) { animTime += dt; nebTime += dt; }
      const wallSec = nowSec;
      packZones();
      rings.update(dt, ringInputs, calm);
      petals.update(nowSec);
      const revealTarget = petals.started ? 1 : 0;
      reveal = calm ? revealTarget : smoothTowards(reveal, revealTarget, dt, 0.5);
      droste = smoothTowards(droste, petals.started ? 1 : 0, dt, 1.2);
      updateMaster(dt);
      refreshLabels();

      // ---- 1. ripple simulation
      if (!calm) {
        for (const p of pointers) {
          if (!p.active) continue;
          const [u, v] = toSimUv(sim, p.x, p.y);
          drops.push(u, v, DROP.dimpleRadius, DROP.dimpleStrength);
        }
        for (let s = 0; s < budgets.rippleSteps; s++) st.ripple.step(drops);
      } else {
        drops.clear();
      }

      // ---- 2. nebula / stars / Droste (half-res)
      const nebEvery = calm ? 4 : budgets.nebulaEvery;
      if (sizeDirty || frameIndex % nebEvery === 0) {
        st.nebula.render({ cssWidth: cssW, cssHeight: cssH, cx: layout.cx, cy: layout.cy, r0: layout.radii[0], R: layout.R,
          timeSec: nebTime, droste: calm ? droste * 0.6 : droste, contrast, theme: scene.theme === 'light' ? 1 : 0, tint });
      }

      // ---- 3. waveform texture (one texSubImage2D)
      st.audio.upload();

      // ---- 4. main mandala pass
      const bloom = st.bloom;
      if (bloom && bloom.scene) gl.bindFramebuffer(gl.FRAMEBUFFER, bloom.scene.fb);
      else gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, devW, devH);
      gl.disable(gl.BLEND);
      st.main.use();
      uploadMainUniforms(gl, st.U, st.ripple, st.audio, st.nebula.texture, cssW, cssH, animTime, wallSec);
      st.quad.draw();

      // ---- 5. bloom (tier 2)
      if (bloom) bloom.resolve(devW, devH, scene.theme === 'light' ? 0.3 : 0.85);

      // ---- 6. particles (tier ≥ 1), additive over the screen
      if (st.particles) {
        const energy = Math.min(1, voiceCount / 4);
        st.particles.update(dt, layout.cx, layout.cy, layout.R, cssW, cssH, animTime, energy, calm);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, devW, devH);
        st.particles.draw(cssW, cssH, dpr, scene.theme === 'light' ? [0.35, 0.12, 0.08] : tint);
      }
      sizeDirty = false;
    },
  };

  // sensible defaults before the first setScene
  renderer.setScene(DEFAULT_SCENE);
  return renderer;
}
